/**
 * @typedef {Object} DebounceOptions
 * @property {boolean} [leading=true] - Whether to invoke the callback function
 * on the leading edge
 * @property {boolean} [trailing=true] - Whether to invoke the callback function
 * on the trailing edge
 *
 * @description
 * Debounces a callback function by a given timeout.
 *
 * Used as a drop-in replacement for lodash's debounce function, in order to
 * reduce bundle size.
 *
 * @param {function} callbackFn - The callback function to debounce
 * @param {number} timeoutMs - The number of milliseconds to debounce the
 * callback function
 * @param {DebounceOptions} [options={}] - The options object
 * @returns {Array} - An array containing the debounced function and the cancel
 * function
 **/
export function debounce(
    callbackFn,
    timeoutMs,
    { leading = false, trailing = true } = {},
) {
    let timeout = null;

    function debounceFn(...args) {
        if (leading && !timeout) {
            callbackFn(...args);
        }
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = window.setTimeout(() => {
            if (trailing) {
                callbackFn(...args);
            }
            timeout = null;
        }, timeoutMs);
    }

    function cancelFn() {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
    }

    return [debounceFn, cancelFn];
}
